import React, { useCallback, useEffect, useLayoutEffect, useState, } from 'react';
import Animated from 'react-native-reanimated';
import { toZonedTime } from 'date-fns-tz';
import { AppView, Button, ButtonGroup, CardView, SettingsItemNavigate, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Section from 'design-system/Section';
import { IconHome } from 'design-system/icons';
import { Amplitude } from 'utils';
import { formatAddress } from 'utils/formatting';
import { postUserAddress, registerUser, updateUserAddress } from 'actions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { DOB_STORAGE_FORMAT } from 'hooks/useDateTimePickerDOB';
import { useAppSelector } from 'store/hooks';
import { selectUser } from 'reducers/selectors';
import { OnboardingFlow } from 'utils/types/navigation';
import { popToOrNavigate } from 'utils/navigationv6';
import { muteErrorAndResult, useFetchOld, useUserAddress } from 'hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { format } from 'date-fns';
import useCreditScoreRegistration from '../hooks/useCreditScoreRegistration';
import CreditScorePersonalDetailsSection from '../components/CreditScorePersonalDetailsSection';
import CreditScoreAddressSection from '../components/CreditScoreAddressSection';
import CreditScoreInvalidAgeModal from '../components/CreditScoreInvalidAgeModal';
import { isValidCreditScoreAge } from '../utils';
const strings = {
    headerTitle: 'Credit score',
    title: 'Review your details',
    subTitle: 'We use this information to search for your credit score',
    personalDetails: 'Personal details',
    currentAddress: 'Current address',
    previousAddress: 'Previous address',
    continueButton: 'Continue',
};
const CreditScoreRegistrationScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const scrollViewStyle = useMarginBottom('paddingBottom', HEIGHT + rem(24), paddingHorizontalStyle);
    const { primaryAddress } = useUserAddress();
    const user = useAppSelector(selectUser);
    const [sectionDob, setSectionDob] = useState();
    // All state from Redux — single source of truth
    const pendingPrimaryAddress = useAppSelector((state) => state.creditScoreOnboarding.pendingPrimaryAddress);
    const pendingPreviousAddress = useAppSelector((state) => state.creditScoreOnboarding.pendingPreviousAddress);
    const pendingMovingDate = useAppSelector((state) => state.creditScoreOnboarding.pendingMovingDate);
    const currentAddress = pendingPrimaryAddress || primaryAddress || undefined;
    const movingDate = pendingMovingDate || currentAddress?.startDate;
    const previousAddress = pendingPreviousAddress;
    const { register, showLoading, hideLoading } = useCreditScoreRegistration();
    const [, , , fetchSave] = useFetchOld(muteErrorAndResult);
    useEffect(() => {
        Amplitude.logEvent('Credit Score Registration Screen');
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    const onPressPreviousAddress = useCallback(() => {
        if (previousAddress && !previousAddress.id) {
            // Pending new address — edit it directly
            navigation.navigate('AddressInputScreen', {
                address: previousAddress,
                flow: OnboardingFlow.CreditScore,
                isAddingSecondaryAddress: true,
            });
            return;
        }
        popToOrNavigate(navigation, 'ChoosePreviousAddressScreen', {
            flow: OnboardingFlow.CreditScore,
        });
    }, [previousAddress]);
    const onPressContinue = useCallback(async () => {
        if (!currentAddress)
            return;
        // Validate DOB before showing any loader so the age modal can surface
        const savedDob = user.dateOfBirth
            ? format(toZonedTime(user.dateOfBirth, 'UTC'), DOB_STORAGE_FORMAT)
            : undefined;
        const dobToCheck = sectionDob ?? savedDob;
        if (!dobToCheck || !isValidCreditScoreAge(dobToCheck)) {
            CreditScoreInvalidAgeModal.show();
            return;
        }
        // Show the same loader the register call uses, so the user sees a
        // single uninterrupted loading state from press → registration result.
        // register() also calls showLoading (idempotent) and hides in its finally.
        showLoading();
        if (sectionDob && sectionDob !== savedDob) {
            const dobRes = await fetchSave(registerUser({ dateOfBirth: sectionDob }));
            if (!dobRes) {
                hideLoading();
                return;
            }
        }
        let primaryAddressId = currentAddress.id;
        if (!primaryAddressId) {
            // New address — POST it with the move-in date
            const result = await fetchSave(postUserAddress({
                ...currentAddress,
                isPrimary: true,
                startDate: movingDate
                    ? format(movingDate, 'yyyy-MM-dd')
                    : undefined,
            }));
            if (!result?.address) {
                hideLoading();
                return;
            }
            primaryAddressId = result.address.id;
        }
        else if (movingDate && currentAddress.startDate !== movingDate) {
            // Existing address — PATCH the move-in date if changed
            const result = await fetchSave(updateUserAddress(primaryAddressId, {
                startDate: format(movingDate, 'yyyy-MM-dd'),
            }));
            if (!result) {
                hideLoading();
                return;
            }
        }
        // If previous address is pending (no id), POST it
        let previousAddressId = previousAddress?.id;
        if (previousAddress && !previousAddressId) {
            const prevResult = await fetchSave(postUserAddress({
                ...previousAddress,
                isPrimary: false,
            }));
            if (!prevResult?.address) {
                hideLoading();
                return;
            }
            previousAddressId = prevResult.address.id;
        }
        register(primaryAddressId, previousAddressId);
    }, [
        currentAddress,
        movingDate,
        previousAddress,
        sectionDob,
        user.dateOfBirth,
        register,
        fetchSave,
        showLoading,
        hideLoading,
    ]);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={scrollViewStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={strings.title} subTitle={strings.subTitle}/>
            <Section>
              <CardView noMarginTop noMarginBottom>
                <CreditScorePersonalDetailsSection onDobChange={setSectionDob}/>
              </CardView>
            </Section>

            <Section title={strings.currentAddress}>
              <CardView noMarginTop noMarginBottom>
                <CreditScoreAddressSection />
              </CardView>
            </Section>

            {!!previousAddress && (<Section title={strings.previousAddress}>
                <CardView noMarginTop noMarginBottom>
                  <SettingsItemNavigate icon={<IconHome gradientKey="lightPink"/>} description="Previous address" title={formatAddress(previousAddress)} onPress={onPressPreviousAddress}/>
                </CardView>
              </Section>)}
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button brand title={strings.continueButton} onPress={onPressContinue}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: strings.headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    headerTintColor: colors.text.primary,
}));
export default CreditScoreRegistrationScreen;
